import { useCallback, useMemo, useState } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import type {
  ParsedJsonUpload,
  PipelineRunMode,
  PipelineStepId,
  PipelineStepState,
} from '../types';
import { PIPELINE_STEP_IDS } from '../types';
import {
  copyJsonToClipboard,
  downloadJsonFile,
  summarizeStepOutput,
  validateStepInput,
} from '../utils';
import {
  createInitialSteps,
  invalidateDownstreamSteps,
} from './pipeline-step-state.utils';

type UsePipelineStepStateOptions = {
  organizationId: string;
  assetId: string;
  uploads: ParsedJsonUpload[];
};

export function usePipelineStepState({
  organizationId,
  assetId,
  uploads,
}: UsePipelineStepStateOptions) {
  const { t } = useTranslation('pipelineDebug');
  const [runMode, setRunMode] = useState<PipelineRunMode>('step-by-step');
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [steps, setSteps] = useState(createInitialSteps);

  const activeStepId = PIPELINE_STEP_IDS[activeStepIndex];
  const activeStep = steps[activeStepId];

  const resetPipeline = useCallback(() => {
    setActiveStepIndex(0);
    setSteps(createInitialSteps());
  }, []);

  const invalidateDownstream = useCallback(
    (
      current: Record<PipelineStepId, PipelineStepState>,
      fromStepId: PipelineStepId,
    ) => invalidateDownstreamSteps(current, fromStepId, uploads),
    [uploads],
  );

  const updateStepInput = useCallback(
    (stepId: PipelineStepId, input: unknown, notify = true) => {
      setSteps((current) => {
        const validation =
          input === null || input === undefined
            ? ({ success: true } as const)
            : validateStepInput(stepId, input);

        const nextStep: PipelineStepState = {
          ...current[stepId],
          input,
          output: null,
          error: validation.success ? null : validation.error,
          status: validation.success
            ? input === null || input === undefined
              ? 'idle'
              : 'ready'
            : 'error',
        };

        return invalidateDownstream(
          {
            ...current,
            [stepId]: nextStep,
          },
          stepId,
        );
      });

      if (notify) {
        message.success(t('messages.inputUpdated'));
      }
    },
    [invalidateDownstream, t],
  );

  const updateStepOutput = useCallback(
    (stepId: PipelineStepId, output: unknown) => {
      setSteps((current) => {
        const nextStep: PipelineStepState = {
          ...current[stepId],
          output,
          error: null,
          status:
            output === null || output === undefined ? 'ready' : 'completed',
        };

        return invalidateDownstream(
          {
            ...current,
            [stepId]: nextStep,
          },
          stepId,
        );
      });
      message.success(t('messages.outputUpdated'));
    },
    [invalidateDownstream, t],
  );

  const downloadStepPayload = useCallback(
    (stepId: PipelineStepId, field: 'input' | 'output') => {
      const payload = steps[stepId][field];
      if (payload === null || payload === undefined) {
        message.warning(
          field === 'input'
            ? t('messages.noInputYet')
            : t('messages.noOutputYet'),
        );
        return;
      }

      downloadJsonFile(`${stepId}-${field}.json`, payload);
    },
    [steps, t],
  );

  const copyStepPayload = useCallback(
    async (stepId: PipelineStepId, field: 'input' | 'output') => {
      const payload = steps[stepId][field];
      const copied = await copyJsonToClipboard(payload);
      if (!copied) {
        message.warning(
          field === 'input'
            ? t('messages.noInputYet')
            : t('messages.noOutputYet'),
        );
        return;
      }

      message.success(t('messages.copied'));
    },
    [steps, t],
  );

  const downloadStepInput = useCallback(
    (stepId: PipelineStepId) => downloadStepPayload(stepId, 'input'),
    [downloadStepPayload],
  );

  const downloadStepOutput = useCallback(
    (stepId: PipelineStepId) => downloadStepPayload(stepId, 'output'),
    [downloadStepPayload],
  );

  const copyStepInput = useCallback(
    (stepId: PipelineStepId) => {
      void copyStepPayload(stepId, 'input');
    },
    [copyStepPayload],
  );

  const copyStepOutput = useCallback(
    (stepId: PipelineStepId) => {
      void copyStepPayload(stepId, 'output');
    },
    [copyStepPayload],
  );

  const canRun =
    Boolean(organizationId) &&
    Boolean(assetId) &&
    activeStep.status !== 'running' &&
    (activeStepId !== 'lin' ||
      uploads.length > 0 ||
      activeStep.input != null) &&
    (activeStep.input != null ||
      activeStepId === 'lin' ||
      steps[PIPELINE_STEP_IDS[activeStepIndex - 1] ?? 'lin']?.output != null);

  const pipelineSummary = useMemo(
    () =>
      PIPELINE_STEP_IDS.map((stepId) => ({
        stepId,
        summary:
          steps[stepId].status === 'completed'
            ? summarizeStepOutput(stepId, steps[stepId].output)
            : null,
      })),
    [steps],
  );

  return {
    runMode,
    setRunMode,
    activeStepIndex,
    setActiveStepIndex,
    activeStepId,
    activeStep,
    steps,
    setSteps,
    resetPipeline,
    updateStepInput,
    updateStepOutput,
    downloadStepInput,
    downloadStepOutput,
    copyStepInput,
    copyStepOutput,
    canRun,
    pipelineSummary,
  };
}
